/** @file historyView: whether undo and redo apply and what they would take back, from the puppet's history. */
import type { EditorState } from './editorTypes';

/** What the Edit menu shows: each side's availability and the label of its next step (cut to 40 characters). */
export type HistoryView = { canUndo: boolean; canRedo: boolean; nextUndoLabel: string | null; nextRedoLabel: string | null };

const MAX_LABEL = 40;

/** A label as the menu shows it: at most 40 characters, the cut marked with an ellipsis. */
const short = (label: string | undefined): string | null => {
  if (label === undefined || label === '') return null;
  return label.length <= MAX_LABEL ? label : `${label.slice(0, MAX_LABEL - 1)}…`;
};

/**
 * No open puppet: nothing applies. An unknown history (null) keeps Undo available, as before the log was read, and
 * Redo off; a known one enables each side only when it has a next step.
 */
export function historyView(state: Pick<EditorState, 'puppet'>): HistoryView {
  const { id, history } = state.puppet;
  if (id === null) return { canUndo: false, canRedo: false, nextUndoLabel: null, nextRedoLabel: null };
  if (history === null) return { canUndo: true, canRedo: false, nextUndoLabel: null, nextRedoLabel: null };
  return {
    canUndo: history.undo !== null, canRedo: history.redo !== null,
    nextUndoLabel: short(history.undo?.label), nextRedoLabel: short(history.redo?.label),
  };
}
